import { createContext, useCallback, useContext, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";

export interface SaveActionState {
  visible: boolean;
  label: string;
  disabled?: boolean;
  title?: string;
  onClick?: () => void;
  icon?: ReactNode;
}

export interface FloatingPanelState {
  saveAction?: SaveActionState | null;
  aiEditNode?: ReactNode | null;
}

interface FloatingPanelContextValue {
  state: FloatingPanelState;
  setFloatingPanelState: Dispatch<SetStateAction<FloatingPanelState>>;
  clearFloatingPanelState: () => void;
}

const FloatingPanelContext = createContext<FloatingPanelContextValue>({
  state: {},
  setFloatingPanelState: () => {
    /* no-op default */
  },
  clearFloatingPanelState: () => {
    /* no-op default */
  },
});

export function FloatingPanelProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FloatingPanelState>({});

  const clearFloatingPanelState = useCallback(() => {
    setState({});
  }, []);

  const value = useMemo(
    () => ({ state, setFloatingPanelState: setState, clearFloatingPanelState }),
    [state, clearFloatingPanelState]
  );

  return <FloatingPanelContext.Provider value={value}>{children}</FloatingPanelContext.Provider>;
}

export function useFloatingPanel() {
  return useContext(FloatingPanelContext);
}
